import { cmsPostIdsSchema } from "@/features/cms";
import { TranslationOverride } from "@/features/internationalisation";
import { inputValuesSchema, riskBandsSchema } from "@/features/risk_bands";
import { trackingConfigSchema } from "@/features/tracking";
import { latLngSchema } from "@/lib/crg_apis/types/latlng";
import { AnaHazardId } from "@/store/services/facades/types";
import { toolSchema } from "@/types/tools";
import { Polygon } from "@turf/turf";
import { ThemeConfig } from "antd/es/config-provider/context";
import { DeepPartial } from "redux";
import { z } from "zod";

// Zod isn't really built for adding React components as types.
const reactComponentFunction = z.function().args(z.any()).returns(z.custom<JSX.Element>());

const portfolioAssetLevelOutputType = z.enum(["small", "tall", "screen", "riskiest"]);

const toggleableFeatureSchema = z.object({
  enabled: z.boolean(),
});

const fallbackHazards: AnaHazardId[] = [
  "coastal_inundation",
  "cyclone_wind",
  "flood_riverine",
  "flood_surfacewater",
  "forest_fire",
  "freeze_thaw",
  "heat",
  "soil_movement",
  "wind",
];

const availableHazards: AnaHazardId[] = [
  "coastal_inundation",
  "cyclone_wind",
  "cyclone_storm_surge",
  "flood_riverine",
  "flood_surfacewater",
  "forest_fire",
  "freeze_thaw",
  "heat",
  "soil_movement",
  "wind",
];

export const hazards = {
  fallback: fallbackHazards,
  available: availableHazards,
};

export const hubThemeMetaSchema = z.object({
  /**
   * Name of platform in the top-right of the UI. If you want to effect the
   * name across the app, change the i18n value at "meta:platformName".
   */
  name: z.string(),
  config: z.object({
    featureSet: z.object({
      /** Covers all forms of user-to-TCRG communication in-Hub. */
      comms: z.object({
        feedback: toggleableFeatureSchema.extend({
          anonymousUser: z.boolean().optional(),
        }),
        support: toggleableFeatureSchema,
        emailSupport: toggleableFeatureSchema.extend({
          email: z.string().email(),
        }),
        reseller: z
          .object({
            ticketRequestForm: toggleableFeatureSchema,
          })
          .optional(),
      }),
      /** Covers all forms of in-Hub documentation. */
      docs: z.object({
        /** Will only be displayed for owners, regardless of enablement. */
        accountManagementTutorial: toggleableFeatureSchema,
        contextualElementTutorials: toggleableFeatureSchema.extend({
          overrides: cmsPostIdsSchema.partial(),
        }),
        knowledgeBase: z.object({
          glossary: toggleableFeatureSchema,
          method: toggleableFeatureSchema.extend({
            /** URL to publicly available methodology documentation - if falsy, will not be shown. */
            publicDocsUrl: z.string().optional(),
          }),
          modelling: toggleableFeatureSchema.extend({
            /** URL to publicly available Climate and Hazard Modelling documentation - if falsy, will not be shown. */
            publicDocsUrl: z.string().optional(),
          }),
          changeLog: toggleableFeatureSchema,
          faqs: toggleableFeatureSchema.extend({
            publicDocsUrl: z.string().optional(),
          }),
        }),
        legal: z.object({
          privacyPolicy: z.string(),
          termsOfService: z.string(),
        }),
        toolPageTutorials: toggleableFeatureSchema,
      }),
      /** TODO: Make settings NOT toggleable. This exists only to restrict pre-v0.6.0-beta */
      settings: toggleableFeatureSchema.extend({
        // Put in anything that we need to hide per-instance.
        // eg. We can enforce HKMA-style invite links here.
      }),
      tools: z.array(toolSchema),
      tracking: trackingConfigSchema,
      widgets: z.object({
        allowDocument360Widget: z.boolean(),
      }),
    }),
    inputConstraints: z.object({
      addressLength: z.object({ min: z.number(), max: z.number() }),
      buildYear: z.object({ min: z.number(), max: z.number() }),
      floorHeight: z.object({ min: z.number(), max: z.number() }),
    }),
    /** Configuration concerning geocoding and its facets. */
    geocoding: z.object({
      /**
       * Whether we allow LatLng input in Quick Batch Screen tool.
       * NOTE: Allows bypassing georestriction in Halo API.
       */
      allowCoordinatesInQuickBatchScreen: z.boolean(),
      /** Whether the user can adjust the order of geocoders. */
      allowReordering: z.boolean(),
      /** Value at which to outright trust a geocoder's result. */
      confidenceThreshold: z.number(),
      /**  */
      coordinateRestrictions: z.object({
        bounds: z.custom<Polygon>().optional(),
        showBoundsOnMap: z.boolean(),
      }),
      /** Where the location picker will be focused on when loaded. */
      defaultMapCentre: latLngSchema,
      /** How zoomed in the location picker will be when loaded. */
      defaultMapZoom: z.number(),
      /** Order in which to try given geocoders. */
      defaultOrder: z.array(z.string()),
      /**
       * Location restrictions, based on Google's geocoder.
       * https://developers.google.com/maps/documentation/javascript/reference/geocoder#GeocoderComponentRestrictions
       * TODO: Change defaultOrder to be array of objects with configuration instead.
       */
      locationRestrictions: z.object({
        administrativeArea: z.string().optional(),
        country: z.string().optional(),
        locality: z.string().optional(),
        postalCode: z.string().optional(),
        route: z.string().optional(),
      }),
      /** Minimum length of text in AddressLookup before loading suggestions. */
      minLengthForSuggestions: z.number(),
    }),
    locale: z.object({
      /** The code of the default currency. */
      defaultCurrency: z.enum(["HKD"]).optional(),
      /** Defaults for form elements which are user-editable. */
      defaultInputValues: inputValuesSchema,
      defaultLanguage: z.string(),
      supportedLanguages: z.array(z.string()),
    }),
    pages: z.object({
      /** Concerns individual pages for collections (Large Site / Portfolio). */
      collections: toggleableFeatureSchema.extend({
        maps: z.object({
          showHazardSelector: z.boolean(),
        }),
      }),
      home: reactComponentFunction,
    }),
    riskBands: riskBandsSchema,
    hazards: z.object({
      available: z.array(z.custom<AnaHazardId>()),
      fallback: z.array(z.custom<AnaHazardId>()),
    }),
    portfolioExport: z.object({
      assetLevelDeepDive: z.object({
        outputTypes: z.array(portfolioAssetLevelOutputType),
      }),
      largeBank: z.object({
        minAddresses: z.number(),
      }),
      maxAddresses: z.number(),
      template: z.object({
        address: z.string(),
        centre: latLngSchema,
      }),
    }),
    tpuLabelling: z
      .object({
        enabled: z.boolean(),
      })
      .optional(),
  }),
  /** Concerns all visually-distinct aspects that aren't covered by Ant Design. */
  aesthetics: z.object({
    /** Whether to show a "Powered by XDI" logo. */
    attribution: z.enum(["none", "poweredByXdi"]),
    /** Concerns sign up / login. */
    authorisation: z.object({
      signUp: z.object({
        warning: z.string().optional(),
      }),
    }),
  }),
  /** TODO: Move all below into "aesthetics" object above. */
  banner: z
    .object({
      accent: z.string().optional(),
    })
    .optional(),
  hasFontStyles: z.boolean().optional(),
  logoUrl: z.string().optional(),
  logoIconUrl: z.string().optional(),
  backgroundImage: z.string().optional(),
  backgroundColor: z.string().optional(),
});

export type PortfolioAssetLevelOutputType = z.infer<typeof portfolioAssetLevelOutputType>;

export type HubThemeMeta = z.infer<typeof hubThemeMetaSchema>;

export interface HubTheme {
  meta: HubThemeMeta;
  antd: ThemeConfig;
  translationOverrides?: Record<string, TranslationOverride>;
}

export type HubThemePartial = DeepPartial<HubTheme>;
